import { matchPath } from 'react-router-dom';
import { AppRoutes } from '@/constants/routes';

export interface RouteConfig {
    readonly parentRoute: AppRoutes | null;
    readonly backToLabelKey: string;
}

export const ROUTE_CONFIG: Readonly<Record<AppRoutes, RouteConfig>> = {
    [AppRoutes.HOME]: {
        parentRoute: null,
        backToLabelKey: 'navigation.backToHome',
    },
    [AppRoutes.SOURCES]: {
        parentRoute: AppRoutes.HOME,
        backToLabelKey: 'navigation.backToSources',
    },
    [AppRoutes.REPORTS]: {
        parentRoute: AppRoutes.HOME,
        backToLabelKey: 'navigation.backToReports',
    },
    [AppRoutes.REPORT]: {
        parentRoute: AppRoutes.REPORTS,
        backToLabelKey: 'navigation.backToReport',
    },
    [AppRoutes.REPORT_EVENT]: {
        parentRoute: AppRoutes.REPORTS,
        backToLabelKey: 'navigation.backToReports',
    },
    [AppRoutes.SOURCE]: {
        parentRoute: AppRoutes.SOURCES,
        backToLabelKey: 'navigation.backToSource',
    },
    [AppRoutes.CAMERAS]: {
        parentRoute: AppRoutes.SOURCES,
        backToLabelKey: 'navigation.backToCameras',
    },
    [AppRoutes.EVENTS]: {
        parentRoute: AppRoutes.SOURCES,
        backToLabelKey: 'navigation.backToEvents',
    },
    [AppRoutes.EVENT]: {
        parentRoute: AppRoutes.EVENTS,
        backToLabelKey: 'navigation.backToEvent',
    },
    [AppRoutes.ALERTS]: {
        parentRoute: AppRoutes.ALERT_RULES,
        backToLabelKey: 'navigation.backToAlerts',
    },
    [AppRoutes.DATABASE_EVENTS]: {
        parentRoute: AppRoutes.SOURCES,
        backToLabelKey: 'navigation.backToSources',
    },
    [AppRoutes.ALERT_RULES]: {
        parentRoute: AppRoutes.HOME,
        backToLabelKey: 'navigation.backToAlertRules',
    },
    [AppRoutes.ALERT_RULE]: {
        parentRoute: AppRoutes.ALERT_RULES,
        backToLabelKey: 'navigation.backToAlertRule',
    },
    [AppRoutes.ALERT_RULE_ALERTS]: {
        parentRoute: AppRoutes.ALERT_RULE,
        backToLabelKey: 'navigation.backToAlerts',
    },
    [AppRoutes.ALERT_RULES_CREATE]: {
        parentRoute: AppRoutes.ALERT_RULES,
        backToLabelKey: 'navigation.backToAlertRules',
    },
    [AppRoutes.ALERT_RULES_EDIT]: {
        parentRoute: AppRoutes.ALERT_RULE,
        backToLabelKey: 'navigation.backToAlertRule',
    },
    [AppRoutes.QUERY_RESULTS]: {
        parentRoute: AppRoutes.SOURCES,
        backToLabelKey: 'navigation.backToExplore',
    },
    [AppRoutes.SETUP_HDMI]: {
        parentRoute: AppRoutes.SOURCES,
        backToLabelKey: 'navigation.backToSources',
    },
    [AppRoutes.SIGN_IN]: {
        parentRoute: null,
        backToLabelKey: 'button.back',
    },
    [AppRoutes.LOGOUT]: {
        parentRoute: null,
        backToLabelKey: 'button.back',
    },
    [AppRoutes.VEHICLE]: {
        parentRoute: AppRoutes.VEHICLES,
        backToLabelKey: 'navigation.backToDirectory',
    },
    [AppRoutes.SETTINGS]: {
        parentRoute: AppRoutes.HOME,
        backToLabelKey: 'navigation.backToSettings',
    },
    [AppRoutes.SETTINGS_MEMBER]: {
        parentRoute: AppRoutes.SETTINGS,
        backToLabelKey: 'navigation.backToSettings',
    },
    [AppRoutes.VEHICLES]: {
        parentRoute: AppRoutes.HOME,
        backToLabelKey: 'navigation.backToVehicles',
    },
    [AppRoutes.PERSONS]: {
        parentRoute: AppRoutes.HOME,
        backToLabelKey: 'navigation.backToPersons',
    },
    [AppRoutes.PERSON]: {
        parentRoute: AppRoutes.PERSONS,
        backToLabelKey: 'navigation.backToPersons',
    },
    [AppRoutes.NOT_FOUND]: {
        parentRoute: null,
        backToLabelKey: 'navigation.backToHome',
    },
} as const;

export function getParentRoute(route: AppRoutes): AppRoutes | null {
    return ROUTE_CONFIG[route]?.parentRoute ?? null;
}

export function getBackToLabel(route: AppRoutes): string {
    return ROUTE_CONFIG[route]?.backToLabelKey ?? 'button.back';
}

function getRouteSpecificity(route: string): number {
    const segments = route.split('/').filter(Boolean);
    const segmentCount = segments.length * 10;
    const staticSegments = segments.filter((s) => !s.startsWith(':')).length;
    return segmentCount + staticSegments;
}

const SORTED_ROUTES = Object.values(AppRoutes)
    .filter((route) => route !== AppRoutes.NOT_FOUND)
    .sort((a, b) => getRouteSpecificity(b) - getRouteSpecificity(a));

export function matchRoutePattern(pathname: string): AppRoutes | null {
    for (const route of SORTED_ROUTES) {
        if (matchPath(route, pathname)) {
            return route;
        }
    }
    return null;
}
